import {
  ActionIcon,
  Button,
  Card,
  CopyButton,
  Divider,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  Badge,
  Tooltip,
  Box,
  Paper
} from '@mantine/core';
import {
  IconBrain,
  IconCalendar,
  IconCheck,
  IconClock,
  IconCopy,
  IconDownload,
  IconEdit,
  IconNotes,
  IconQrcode
} from '@tabler/icons-react';
import QRCode from 'qrcode';
import React, { useEffect, useState } from 'react';
import { formatLocalDate } from 'utils/convertLocalFormat';

interface DetailInformationSectionProps {
  assessment: {
    startTime: string;
    endTime: string;
    notificationSchedule: string;
    linkExternal: string;
    modules: any[];
    participants: any[];
    notes: string;
    smes: any[];
  };
  activeModuleIndex: number;
  setActiveModuleIndex: any;
  handleNotes?: any;
  handleDownload: any;
  handleEdit: any;
}

const DetailInformationSection: React.FC<DetailInformationSectionProps> = ({
  assessment,
  activeModuleIndex,
  setActiveModuleIndex,
  handleNotes,
  handleDownload,
  handleEdit
}) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [isGeneratingQr, setIsGeneratingQr] = useState<boolean>(false);

  useEffect(() => {
    // Reset QR when the link changes
    setQrCodeDataUrl('');
  }, [assessment.linkExternal]);

  const generateQRCode = async (url: string): Promise<string> => {
    try {
      setIsGeneratingQr(true);
      const qrDataUrl = await QRCode.toDataURL(url, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });
      return qrDataUrl;
    } catch {
      throw new Error('Failed to generate QR code');
    } finally {
      setIsGeneratingQr(false);
    }
  };

  const downloadQRCode = async () => {
    try {
      if (!assessment.linkExternal) return;
      let dataUrl = qrCodeDataUrl;
      if (!dataUrl) {
        dataUrl = await generateQRCode(assessment.linkExternal);
        setQrCodeDataUrl(dataUrl);
      }
      if (!dataUrl) return;
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = 'assessment-qr.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      // no-op
    }
  };
  return (
    <Card withBorder radius='lg' padding='lg' pos={'sticky'} top={16}>
      <Stack gap='xs'>
        <Group gap='xs' align='center'>
          <IconClock size={20} />
          <Title order={4}>Timeline</Title>
        </Group>

        <Group gap='xs' wrap='nowrap'>
          <IconCalendar size={20} />
          <Stack gap={0}>
            <Text size='sm' fw={500}>
              Start Date
            </Text>
            <Text size='sm'>{formatLocalDate(assessment?.startTime, 'MMM D, YYYY HH:mm')}</Text>
          </Stack>
        </Group>

        <Group gap='xs' wrap='nowrap'>
          <IconCalendar size={20} />
          <Stack gap={0}>
            <Text size='sm' fw={500}>
              End Date
            </Text>
            <Text size='sm'>{formatLocalDate(assessment?.endTime, 'MMM D, YYYY HH:mm')}</Text>
          </Stack>
        </Group>

        <Divider my='md' />

        <Title order={4}>Modules</Title>
        <Group>
          {assessment.modules?.map((module: any, index: number) => (
            <Badge
              radius={'sm'}
              h={28}
              variant={activeModuleIndex === index ? 'filled' : 'outline'}
              color={activeModuleIndex === index ? 'primary' : 'gray'}
              key={index}
              style={{ cursor: 'pointer' }}
              onClick={() => setActiveModuleIndex(index)}
            >
              {module.moduleName || `Module ${index + 1}`}
            </Badge>
          ))}
        </Group>

        {assessment.smes && assessment.smes.length > 0 && (
          <>
            <Divider my='md' />
            <Title order={4}>Subject Matter Experts</Title>
            <Group gap='xs' align='center'>
              <IconBrain size={20} />
              <Text fw={500}>
                {assessment.smes.length} SME{assessment.smes.length !== 1 ? 's' : ''}
              </Text>
            </Group>
          </>
        )}
        {assessment.linkExternal && (
          <>
            <Divider my='md' />
            <Title order={4}>Share Assessment</Title>
            <Group w={'100%'}>
              <TextInput
                value={assessment.linkExternal}
                readOnly
                w={'100%'}
                rightSection={
                  <CopyButton value={assessment.linkExternal} timeout={2000}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position='right'>
                        <ActionIcon color={copied ? 'teal' : 'gray'} onClick={copy}>
                          {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                }
              />
            </Group>
            <Paper withBorder p='md' mt='md'>
              <Stack gap='md' align='center'>
                <Group justify='space-between' w='100%'>
                  <Text fw={500} size='sm'>
                    QR Code for Quick Access
                  </Text>
                  <Button
                    size='xs'
                    variant='outline'
                    leftSection={<IconDownload size={14} />}
                    onClick={downloadQRCode}
                    loading={isGeneratingQr}
                    disabled={!assessment.linkExternal}
                  >
                    Download QR
                  </Button>
                </Group>

                {/* QR Code Display */}
                {qrCodeDataUrl ? (
                  <Box
                    style={{
                      padding: '12px',
                      backgroundColor: '#ffffff',
                      borderRadius: '8px',
                      border: '2px solid #e9ecef'
                    }}
                  >
                    <img
                      src={qrCodeDataUrl}
                      alt='Assessment QR Code'
                      style={{
                        width: '150px',
                        height: '150px',
                        display: 'block'
                      }}
                    />
                  </Box>
                ) : isGeneratingQr ? (
                  <Box
                    style={{
                      width: '150px',
                      height: '150px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '2px dashed #dee2e6'
                    }}
                  >
                    <Stack align='center' gap='xs'>
                      <IconQrcode size={32} color='#6c757d' />
                      <Text size='xs' c='dimmed'>
                        Generating...
                      </Text>
                    </Stack>
                  </Box>
                ) : (
                  <Box
                    style={{
                      width: '150px',
                      height: '150px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '2px dashed #dee2e6',
                      cursor: 'pointer'
                    }}
                    onClick={() => generateQRCode(assessment.linkExternal).then(setQrCodeDataUrl)}
                  >
                    <Stack align='center' gap='xs'>
                      <IconQrcode size={32} color='#6c757d' />
                      <Text size='xs' c='dimmed'>
                        Click to generate
                      </Text>
                    </Stack>
                  </Box>
                )}

                <Text size='xs' c='dimmed' ta='center'>
                  Others can scan this QR code to access the assessment directly
                </Text>
              </Stack>
            </Paper>
          </>
        )}

        <Divider my='md' />

        <Stack>
          <Button leftSection={<IconDownload size={16} />} fullWidth onClick={handleDownload}>
            Download Data
          </Button>
          <Button
            leftSection={<IconEdit size={16} />}
            variant='outline'
            fullWidth
            onClick={handleEdit}
          >
            Edit Assessment
          </Button>
          {handleNotes && (
            <Button
              leftSection={<IconNotes size={16} />}
              variant='outline'
              fullWidth
              onClick={handleNotes}
            >
              Assessment Notes
            </Button>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

export default DetailInformationSection;
